/* eslint-disable security/detect-object-injection */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    LOCALIZATION_DEFAULT_LOCALE,
    LOCALIZATION_DEFAULT_PREFERRED_UNIT,
    LOCALIZATION_DEFAULT_TIMEZONE
} from '../../configuration/localization';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { IUserInfo } from './post-localization.action';
/**
 * GetLocalization Input Action
 */
export class GetLocalizationInput implements Msdyn365.IActionInput {
    public timestamp: string;
    public userToken: string;
    constructor(args: Msdyn365.ICreateActionContext) {
        this.timestamp = new Date().getTime().toString();
        this.userToken = args.requestContext.user.token;
    }

    public getCacheKey = () => `GetLocalizationKey-${this.timestamp}-${this.userToken}`;
    public getCacheObjectType = () => 'GetLocalization';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IGetLocalizationData {
    numberLocale: INumberFormat;
    dateLocale: string;
    preferredUnit?: string;
    timeZone: string;
}

export interface INumberFormat {
    thousandSeparator: Separator;
    decimalSeparator: Separator;
}

export enum Separator {
    Dot = '.',
    Comma = ','
}

export enum NumberLocale {
    DE = '###.###,000',
    EN = '###,###.000'
}

export const numberFormats: { [key: string]: INumberFormat } = {
    'en-US': { thousandSeparator: Separator.Comma, decimalSeparator: Separator.Dot },
    'de-DE': { thousandSeparator: Separator.Dot, decimalSeparator: Separator.Comma }
};

export const mapNumberFormatToLocale = (numberFormat: INumberFormat): string => {
    if (numberFormat.thousandSeparator === Separator.Dot && numberFormat.decimalSeparator === Separator.Comma) {
        return 'de-DE';
    } else if (numberFormat.thousandSeparator === Separator.Comma && numberFormat.decimalSeparator === Separator.Dot) {
        return 'en-US';
    } else {
        throw new Error('Unsupported number format');
    }
};

export function getNumberFormatByLocale(locale: string): INumberFormat | undefined {
    const localeMap: { [key: string]: string } = {
        EN: 'en-US',
        DE: 'de-DE'
    };

    const mappedLocale = localeMap[locale];
    return numberFormats[mappedLocale];
}

export const separatorFormats: { [key in Separator]: INumberFormat } = {
    [Separator.Comma]: { thousandSeparator: Separator.Dot, decimalSeparator: Separator.Comma },
    [Separator.Dot]: { thousandSeparator: Separator.Comma, decimalSeparator: Separator.Dot }
};

export enum PreferredUnit {
    KG = 'KG',
    LB = 'LB',
    Liter = 'Liter'
}

export enum DateLocale {
    DE = 'de-DE',
    US = 'en-US',
    GB = 'en-GB'
}

const dateFormats: { [key in DateLocale]: string } = {
    [DateLocale.DE]: 'dd.mm.yyyy',
    [DateLocale.US]: 'mm/dd/yyyy',
    [DateLocale.GB]: 'dd/mm/yyyy'
};

export const getNumberFormat = (locale: NumberLocale | Separator): INumberFormat => {
    return numberFormats[locale];
};

export const getDateFormat = (locale: DateLocale): string => {
    return dateFormats[locale];
};

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetLocalizationInput(args);
};

async function action(_: GetLocalizationInput, ctx: Msdyn365.IActionContext): Promise<IGetLocalizationData> {
    if (!ctx.requestContext.params.isEditor) {
        ensureAuthenticated(ctx);
    }
    const fallbackLocalization: IGetLocalizationData = {
        dateLocale: LOCALIZATION_DEFAULT_LOCALE,
        numberLocale: { thousandSeparator: Separator.Comma, decimalSeparator: Separator.Dot },
        preferredUnit: LOCALIZATION_DEFAULT_PREFERRED_UNIT,
        timeZone: LOCALIZATION_DEFAULT_TIMEZONE
    };

    const response = await Msdyn365.sendRequest(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetUserInfo`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`,
                'Accept-Language': LOCALIZATION_DEFAULT_LOCALE
            }
        }
    );

    if (response.status === 200 && response.data) {
        const responseData = response.data as IUserInfo;

        return {
            dateLocale: responseData.DateFormat,
            numberLocale: numberFormats[responseData.NumberFormat],
            preferredUnit: responseData.PreferredUnit,
            timeZone: responseData.TimeZone ?? fallbackLocalization.timeZone
        } as IGetLocalizationData;
    } else {
        return fallbackLocalization;
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetLocalizationData>>action,
    id: 'GetLocalization',
    input: createInput
});
